/**
 * Centralized route path constants for programmatic navigation
 * Use these constants instead of hardcoded strings
 */

/**
 * Route paths
 */
export const PATHS = {
  // Public routes
  ROOT: '/',

  // Auth routes
  LOGIN: '/auth/login',
  CALLBACK: '/auth/callback',
  SIGNUP: '/auth/signup',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',

  // App routes
  DASHBOARD: '/app/dashboard',
  USERS: '/app/users',
  USER_DETAILS: (id: string) => `/app/users/${id}`,
  USER_CREATE: '/app/users/create',
  USER_EDIT: (id: string) => `/app/users/${id}/edit`,
  GATEWAY_TYPES: '/app/gateway-types',
  DEVICE_TYPES: '/app/device-types',
  MQTT: '/app/mqtt',
  MQTT_CREATE: '/app/mqtt/create',
  MQTT_DETAILS: (id: string) => `/app/mqtt/${id}`,
  MQTT_EDIT: (id: string) => `/app/mqtt/${id}/edit`,
  GATEWAY_TYPE_CREATE: '/app/gateway-types/create',
  GATEWAY_TYPE_DETAILS: (id: string) => `/app/gateway-types/${id}`,
  GATEWAY_TYPE_EDIT: (id: string) => `/app/gateway-types/${id}/edit`,
  DEVICE_TYPE_CREATE: '/app/device-types/create',
  DEVICE_TYPE_DETAILS: (id: string) => `/app/device-types/${id}`,
  DEVICE_TYPE_EDIT: (id: string) => `/app/device-types/${id}/edit`,
  PROJECTS: '/app/projects',
  STORAGE: '/app/storage',

  // Settings
  SETTINGS: '/app/settings',
  PROFILE: '/app/profile',
  NOTIFICATIONS: '/app/notifications',

  // Error pages
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',
  SERVER_ERROR: '/500',
} as const;

/**
 * Route groups for navigation and access control
 */
export const ROUTE_GROUPS = {
  PUBLIC: [PATHS.ROOT, PATHS.LOGIN, PATHS.SIGNUP, PATHS.FORGOT_PASSWORD, PATHS.RESET_PASSWORD],
  AUTH: [PATHS.LOGIN, PATHS.CALLBACK, PATHS.SIGNUP, PATHS.FORGOT_PASSWORD, PATHS.RESET_PASSWORD],
  APP: [
    PATHS.DASHBOARD,
    PATHS.USERS,
    PATHS.GATEWAY_TYPES,
    PATHS.DEVICE_TYPES,
    PATHS.PROJECTS,
    PATHS.STORAGE,
    PATHS.SETTINGS,
    PATHS.PROFILE,
    PATHS.NOTIFICATIONS,
  ],
  ERROR: [PATHS.NOT_FOUND, PATHS.UNAUTHORIZED, PATHS.SERVER_ERROR],
} as const;

/**
 * Navigation items for menu/sidebar
 */
export const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: PATHS.DASHBOARD,
    icon: 'LayoutDashboard',
  },
  {
    label: 'Users',
    path: PATHS.USERS,
    icon: 'Users',
  },
  {
    label: 'Settings',
    path: PATHS.SETTINGS,
    icon: 'Settings',
  },
  {
    label: 'Profile',
    path: PATHS.PROFILE,
    icon: 'User',
  },
  {
    label: 'Notifications',
    path: PATHS.NOTIFICATIONS,
    icon: 'Bell',
  },
] as const;

/**
 * Helper to check if a path matches a route pattern
 */
export function matchesRoute(currentPath: string, routePath: string): boolean {
  // Handle exact matches
  if (currentPath === routePath) return true;

  // Handle dynamic segments (e.g., /app/users/:id)
  const routePattern = routePath.replace(/:[^/]+/g, '[^/]+');
  const regex = new RegExp(`^${routePattern}$`);
  return regex.test(currentPath);
}

/**
 * Helper to extract params from a dynamic route
 */
export function extractParams(currentPath: string, routePath: string): Record<string, string> {
  const params: Record<string, string> = {};
  const routeParts = routePath.split('/');
  const pathParts = currentPath.split('/');

  routeParts.forEach((part, index) => {
    if (part.startsWith(':')) {
      const paramName = part.slice(1);
      params[paramName] = pathParts[index];
    }
  });

  return params;
}
